/**
 * Round 20 U9 -- "Is it safe right now?" answered with a list.
 *
 * Open critical and high findings, and every incident an operator has
 * raised, newest first, each with what it is on. The cascade orders by
 * entity; a reader who has been told something is wrong wants what
 * arrived last at the top.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { api } from "shared/api/client";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { formatDateTime } from "shared/time/formatDate";

export interface OpenFinding {
  id: string;
  title: string;
  severity: string;
  raised_at: string;
  entity_kind: string;
  entity_id: string;
  entity_name: string | null;
  source_label: string | null;
  is_incident: boolean;
}

const SEVERITY_TONE: Record<string, string> = {
  critical: "bg-rag-redBg text-rag-red",
  high: "bg-rag-amberBg text-rag-amber",
};

export function OpenFindingsView(): JSX.Element {
  const { t, i18n } = useTranslation();
  // Round 20 Q7 -- a service's "See them" lands here filtered to it.
  const [params] = useSearchParams();
  const service = params.get("service");
  const list = useQuery({
    queryKey: ["twin", "open-findings", service],
    queryFn: () =>
      api.get<OpenFinding[]>(
        service
          ? `/v1/open-findings?service=${encodeURIComponent(service)}`
          : "/v1/open-findings",
      ),
    refetchInterval: 60_000,
  });
  const rows = list.data ?? [];
  return (
    <section data-testid="open-findings-view" className="flex flex-col gap-4">
      <header>
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="finding"
            size={22}
            className="me-2 inline-block align-[-0.12em] text-brand-brassDark"
          />
          {t(
            service
              ? "modules.twin.openFindings.serviceHeading"
              : "modules.twin.openFindings.heading",
          )}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          {t(
            service
              ? "modules.twin.openFindings.serviceIntro"
              : "modules.twin.openFindings.intro",
          )}
        </p>
        {service && (
          <p
            data-testid="open-findings-service-filter"
            className="mt-1 flex gap-3 text-sm"
          >
            <Link
              to={`/twin/service/${service}`}
              className="text-brand-brassDark hover:underline"
            >
              {t("modules.twin.openFindings.backToService")}
            </Link>
            <Link to="/twin/findings" className="text-brand-brassDark hover:underline">
              {t("modules.twin.openFindings.showAll")}
            </Link>
          </p>
        )}
      </header>
      {list.isLoading && (
        <p className="text-sm text-slate-500">
          {t("modules.twin.openFindings.loading")}
        </p>
      )}
      {list.isError && (
        <p className="text-sm text-rag-red">{t("modules.twin.openFindings.error")}</p>
      )}
      {list.isSuccess && rows.length === 0 && (
        <p data-testid="open-findings-empty" className="text-sm text-slate-500">
          {t(
            service
              ? "modules.twin.openFindings.serviceEmpty"
              : "modules.twin.openFindings.empty",
          )}
        </p>
      )}
      {rows.length > 0 && (
        <ol data-testid="open-findings-list" className="flex flex-col gap-1.5">
          {rows.map((f) => (
            <li
              key={f.id}
              data-testid={`open-finding-${f.id}`}
              className="flex flex-wrap items-baseline gap-x-2 text-sm"
            >
              <span className="tabular-nums text-slate-500">
                {formatDateTime(f.raised_at, i18n.language)}
              </span>
              <span
                className={`rounded px-1.5 text-xs font-medium ${SEVERITY_TONE[f.severity] ?? "bg-slate-100 text-slate-600"}`}
              >
                {t(`modules.twin.openFindings.severity.${f.severity}`)}
              </span>
              {f.is_incident && (
                <span className="rounded bg-slate-100 px-1.5 text-xs text-slate-700">
                  {t("modules.twin.openFindings.incident")}
                </span>
              )}
              <Link
                to={`/twin/findings/${f.id}`}
                className="font-medium text-slate-800 hover:underline"
              >
                {f.title}
              </Link>
              {f.entity_name && (
                <span className="text-slate-500">
                  {t("modules.twin.openFindings.on", { name: f.entity_name })}
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
